import { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { FirstTimeUser, CompanyList, CreateCompany } from './company/components';
import { companyService } from '../services/companyService';
import type { Company } from '../services/companyService';

export default function CompaniesPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const result = await companyService.getAll(true);
      setCompanies(result.length !== 0 ? result : []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const visibleCompanies = showOnlyActive
    ? companies.filter((company) => !company.deactivatedDate)
    : companies;

  const handleCreateCompany = () => {
    setShowCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false);
  };

  const handleCompanyCreated = (newCompany: Company) => {
    setCompanies(prev => [...prev, newCompany]);
    setShowCreateDialog(false);
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!window.confirm('Ви впевнені, що хочете архівувати цю компанію?')) {
      return;
    }

    await companyService.delete(companyId);
    setCompanies(prev => prev.map(c => (
      c.id === companyId ? { ...c, deactivatedDate: new Date().toISOString() } : c
    )));
  };

  const handleRestoreCompany = async (companyId: number) => {
    await companyService.restore(companyId);
    setCompanies(prev => prev.map(c => (
      c.id === companyId ? { ...c, deactivatedDate: null } : c
    )));
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (companies.length === 0) {
    return (
      <>
        <FirstTimeUser onCreateCompany={handleCreateCompany} />
        <CreateCompany
          open={showCreateDialog}
          onClose={handleCloseCreateDialog}
          onCompanyCreated={handleCompanyCreated}
        />
      </>
    );
  }

  return (
    <>
      <CompanyList
        companies={visibleCompanies}
        onCreateCompany={handleCreateCompany}
        onDeleteCompany={handleDeleteCompany}
        onRestoreCompany={handleRestoreCompany}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(prev => !prev)}
        showOnlyActive={showOnlyActive}
        onShowOnlyActiveChange={setShowOnlyActive}
      />
      <CreateCompany
        open={showCreateDialog}
        onClose={handleCloseCreateDialog}
        onCompanyCreated={handleCompanyCreated}
      />
    </>
  );
}
